<template>

    <div id="login" class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-md text-brand-500">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" preserveAspectRatio="none" class="fill-current h-14 mx-auto">
                <path d="M476.791 135.986L250.075 5.818 50.9 120.812l425.765 245.379V283.2l-288.49-165.674 58.07-33.522 230.546 132.357zM23.209 136.478l423.717 244.204-196.635 113.499L23.578 363.287v-77.109l228.319 131.107 58.05-33.506L23.209 219.091z"></path>
            </svg>
            <h2 class="mt-2 text-center text-3xl text-gray-900">
                {{ app_name }}
            </h2>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="mb-4 flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-2 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasErrorOccurred()">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p class="text-error-700 text-sm">
                    {{ form.getErrorMessage() }}
                </p>
            </div>

            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" v-if="form.mfa && form.mfa_recovery === false">
                <form class="space-y-6" action="#" method="POST" @submit.prevent="attemptMultiFactorAuthenticationCheck">

                    <h3 class="font-medium mb-4">Two Factor Authentication</h3>

                    <p class="text-sm text-gray-600">
                        Enter your Two Factor Authentication Token. Unable to access authentication device? Use a <a href="#" @click.prevent="form.mfaRecovery()">recovery code</a>.
                    </p>

                    <div class="form-control" :class="{ 'form-control-error': form.fieldHasErrors('code') }">
                        <input id="code" name="code" type="text" maxlength="6" required v-model="form.data.code">
                        <span v-if="form.fieldHasErrors('code')">{{ form.getFieldError('code') }}</span>
                    </div>

                    <p class="text-sm text-right">

                    </p>

                    <div>
                        <button type="submit" class="btn w-full justify-center">
                            Submit
                        </button>
                    </div>

                    <div class="mt-4 text-sm text-right">
                        <p>
                            <a href="/login">Go Back</a>
                        </p>
                    </div>
                </form>
            </div>

            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" v-else-if="form.mfa && form.mfa_recovery === true">
                <form class="space-y-6" action="#" method="POST" @submit.prevent="attemptMultiFactorAuthenticationCheck">

                    <h3 class="font-medium mb-4">Two Factor Recovery</h3>

                    <p class="text-sm text-gray-600">
                        Enter a Two Factor Authentication Recovery Code.
                    </p>

                    <div class="form-control" :class="{ 'form-control-error': form.fieldHasErrors('recovery_code') }">
                        <input id="recovery_code" name="recovery_code" type="text" required v-model="form.data.recovery_code">
                        <span v-if="form.fieldHasErrors('recovery_code')">{{ form.getFieldError('recovery_code') }}</span>
                    </div>

                    <div>
                        <button type="submit" class="btn w-full justify-center">
                            Submit
                        </button>
                    </div>

                    <div class="mt-4 text-sm text-right">
                        <p>
                            <a href="/login">Go Back</a>
                        </p>
                    </div>
                </form>
            </div>

            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" v-else>
                <form class="space-y-6" action="#" method="POST" @submit.prevent="attemptLogin">
                    <div class="form-control" :class="{ 'form-control-error': form.fieldHasErrors('email') }">
                        <label for="email">Email address</label>
                        <input id="email" name="email" type="email" autocomplete="email" required v-model="form.data.email">
                        <span v-if="form.fieldHasErrors('email')">{{ form.getFieldError('email') }}</span>
                    </div>

                    <div class="form-control relative" :class="{ 'form-control-error': form.fieldHasErrors('password') }">
                        <label for="password">Password</label>
                        <input id="password" name="password" :type="form.passwordFieldType" autocomplete="current-password" class="static" required v-model="form.data.password">
                        <button type="button" class="absolute top-9 right-2 z-50 text-gray-400" @click.prevent="form.togglePasswordFieldType()">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" v-if="form.passwordFieldType === 'password'">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" v-else>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                        </button>
                        <span v-if="form.fieldHasErrors('password')">{{ form.getFieldError('password') }}</span>
                    </div>

                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <input id="remember_me" name="remember_me" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" v-model="form.data.remember">
                            <label for="remember_me" class="ml-2 block text-sm text-gray-900">
                                Remember me
                            </label>
                        </div>

                        <div class="text-sm">
                            <router-link :to="{ name: 'recover' }">
                                Forgot your password?
                            </router-link>
                        </div>
                    </div>

                    <div>
                        <button type="submit" class="btn w-full justify-center">
                            Sign In
                        </button>
                    </div>
                </form>

                <div class="mt-6">
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300"></div>
                        </div>
                        <div class="relative flex justify-center text-sm">
                    <span class="px-2 bg-white text-gray-500">
                      Or
                    </span>
                        </div>

                    </div>
                </div>

                <div class="mt-6">
                    <router-link :to="{ name: 'signup' }" class="btn-outline w-full justify-center">
                        Sign Up
                    </router-link>
                </div>
            </div>

            <div class="text-center w-full text-xs text-gray-400 mt-2">
                <p class="mt-4">{{ app_name }} {{ version }} &copy; <a :href="copyright.url" class="text-brand-400" target="_blank">{{ copyright.name }}</a></p>
                <p class="my-2">Unauthorised access is not permitted. <a :href="status_uri" class="text-brand-400" target="_blank" v-if="status_uri">System Status</a></p>
            </div>
        </div>
    </div>

</template>

<script>
import Vue from "vue";
import {PermissionMixin} from "../../support/mixins";
import {SanctumAuthentication} from "../../app/Auth/SanctumAuthentication";

export default Vue.extend({
    name: "Login",
    mixins: [PermissionMixin],
    props: {
        app_name: {
            type: String,
            required: false
        },
        version: {
            type: String,
            required: false
        },
        status_uri: {
            type: String,
            required: false
        },
        copyright: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            form: new SanctumAuthentication(),
        }
    },
    methods: {
        attemptLogin() {
            this.form.signIn().then(() => {
                if (this.form.mfa === false) {
                    this.performAuthenticationRedirection()
                }
            });
        },
        attemptMultiFactorAuthenticationCheck() {
            this.form.sendMfaCheck().then(() => {
                this.performAuthenticationRedirection()
            });
        },
        performAuthenticationRedirection() {
            let redirect = this.getAuthRedirect();

            if (redirect) {
                this.clearAuthRedirect();

                this.$router.push({path: redirect});
            } else {
                this.$router.go(0);
            }
        }
    }
});
</script>


